import { DictionaryIrregularVerb } from 'app/types';
import { FormType } from './patternSuperGroups';

export interface Aggregate {
  total: number;
  learned: number;
  /**
   * Count of verbs the user has in their vocabulary, however they got
   * there: anything with `isLearned` OR `isActiveSpeech` set. Drives the
   * "added" segment of the progress bar (so the bar shows actual coverage
   * instead of pretending every verb is already in the user's vocab).
   */
  added: number;
}

const empty = (): Aggregate => ({ total: 0, learned: 0, added: 0 });
const add = (acc: Aggregate, v: DictionaryIrregularVerb): Aggregate => ({
  total: acc.total + 1,
  learned: acc.learned + (v.isLearned ? 1 : 0),
  added: acc.added + (v.isLearned || v.isActiveSpeech ? 1 : 0),
});

/**
 * Returns a map keyed by `keyFn(verb)` (skipping verbs where keyFn returns null)
 * with total + learned counts per key.
 */
export function aggregateBy<K extends string>(
  verbs: readonly DictionaryIrregularVerb[],
  keyFn: (v: DictionaryIrregularVerb) => K | null,
): Map<K, Aggregate> {
  const out = new Map<K, Aggregate>();
  for (const v of verbs) {
    const key = keyFn(v);
    if (key === null) continue;
    const current = out.get(key) ?? empty();
    out.set(key, add(current, v));
  }
  return out;
}

export const aggregateByPattern = (verbs: readonly DictionaryIrregularVerb[]): Map<string, Aggregate> =>
  aggregateBy(verbs, (v) => v.pattern ?? null);

export const aggregateByForm = (verbs: readonly DictionaryIrregularVerb[]): Map<FormType, Aggregate> =>
  aggregateBy<FormType>(verbs, (v) => (v.formType as FormType | undefined) ?? null);

export const aggregateByBase = (verbs: readonly DictionaryIrregularVerb[]): Map<string, Aggregate> =>
  aggregateBy(verbs, (v) => (v.baseVerb && v.baseVerb.length > 0 ? v.baseVerb : null));

export const aggregateByLevel = (verbs: readonly DictionaryIrregularVerb[]): Map<string, Aggregate> =>
  aggregateBy(verbs, (v) => v.levelBased ?? null);

export function totals(verbs: readonly DictionaryIrregularVerb[]): Aggregate {
  return verbs.reduce((acc, v) => add(acc, v), empty());
}
